import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import {
  ThemeProvider,
  SettingsProvider,
  AuthProvider,
  SessionTimeoutProvider,
  AppShell,
  AuthenticatedRoute,
  ClientRoute,
  ModuleRoute,
  AdminRoute,
  AdminMfaPrompt,
  SecurityOwnerRoute,
  type ShellNavItem,
} from '@ddc-gds/sax-hub-core';
import {
  BarChart3,
  BadgeDollarSign,
  Bot,
  Building2,
  CalendarClock,
  ClipboardList,
  FileClock,
  FileText,
  Home,
  Kanban,
  Key,
  LayoutDashboard,
  List,
  Landmark,
  Receipt,
  Server,
  Settings,
  Shield,
  ShoppingCart,
  Truck,
  Users,
  UserRoundCog,
  WalletCards,
} from 'lucide-react';
import {
  AdminClientDetailPage,
  AdminClientListPage,
  AdminAuditLogPage,
  AdminBusinessAgentsPage,
  AdminDashboardPage,
  AdminInstalledApisPage,
  AdminModuleLayout,
  AdminPermissionListPage,
  AdminRoleListPage,
  AdminUserListPage,
  SaxAdminSecurityPage,
  BusinessAgentsHomePage,
  BusinessAgentsModuleLayout,
  FinanceCashflowRoute,
  ProductionDashboardPage,
  ProductionKanbanPage,
  ProductionListPage,
  ProductionModuleLayout,
  ProductionReportsPage,
  ProductionSettingsPage,
  ProductionStationsPage,
} from './lazyModules';

import { HubHomePage } from './pages/HubHomePage';
import { LoginPage } from './pages/LoginPage';

const PRODUCTION_MODULE_ID = 'PRODUCCION';
const PRODUCTION_ACCESS_PERMISSIONS = ['produccion:access'];
const BUSINESS_AGENTS_MODULE_ID = 'BUSINESS_AGENTS';
const BUSINESS_AGENTS_ACCESS_PERMISSIONS = ['business_agents:access'];
const FINANCE_MODULE_ID = 'FINANZAS';
const FINANCE_ACCESS_PERMISSIONS = ['finanzas:access'];
const SAX_ADMIN_SECURITY_ENABLED = import.meta.env.VITE_SAX_ADMIN_SECURITY_ENABLED === 'true';

const hubHomeNavItem: ShellNavItem = {
  path: '/',
  label: 'SAX Hub',
  icon: Home,
  roles: ['admin', 'client'],
  end: true,
};

const productionNavItems: ShellNavItem[] = [
  { path: '/dashboard', label: 'Producción', icon: LayoutDashboard, roles: ['client'], module: PRODUCTION_MODULE_ID, requiredPermissions: PRODUCTION_ACCESS_PERMISSIONS, end: true },
  { path: '/kanban', label: 'Kanban', icon: Kanban, roles: ['client'], module: PRODUCTION_MODULE_ID, requiredPermissions: PRODUCTION_ACCESS_PERMISSIONS },
  { path: '/list', label: 'Lista', icon: List, roles: ['client'], module: PRODUCTION_MODULE_ID, requiredPermissions: PRODUCTION_ACCESS_PERMISSIONS },
  { path: '/reports', label: 'Reportes', icon: BarChart3, roles: ['client'], module: PRODUCTION_MODULE_ID, requiredPermissions: PRODUCTION_ACCESS_PERMISSIONS },
];

const productionFooterNavItems: ShellNavItem[] = [
  { path: '/settings', label: 'Settings', icon: Settings, roles: ['client'], module: PRODUCTION_MODULE_ID, requiredPermissions: PRODUCTION_ACCESS_PERMISSIONS },
];

const businessAgentsNavItems: ShellNavItem[] = [
  {
    path: '/agentes',
    label: 'Agentes SAX',
    icon: Bot,
    roles: ['client'],
    module: BUSINESS_AGENTS_MODULE_ID,
    requiredPermissions: BUSINESS_AGENTS_ACCESS_PERMISSIONS,
    end: true,
  },
];

const financeNavItems: ShellNavItem[] = [
  { path: '/finanzas', label: 'Cashflow', icon: WalletCards, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/valores-cartera', label: 'Valores cartera', icon: BadgeDollarSign, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/ventas-futuras', label: 'Ventas futuras', icon: CalendarClock, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/remitos-pendientes', label: 'Remitos', icon: Truck, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/facturas-pendientes', label: 'Facturas', icon: FileText, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/pedidos-clientes', label: 'Pedidos', icon: ClipboardList, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/saldo-proveedores', label: 'Proveedores', icon: Building2, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/ordenes-compra-proveedores', label: 'Ordenes de compra', icon: ShoppingCart, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/procesos', label: 'Procesos', icon: Kanban, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/cheques-emitidos', label: 'Cheques emitidos', icon: Receipt, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/leasing', label: 'Leasing', icon: Landmark, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/creditos-afip', label: 'Creditos AFIP', icon: FileClock, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
  { path: '/finanzas/gastos-fijos', label: 'Gastos fijos', icon: List, roles: ['client'], module: FINANCE_MODULE_ID, requiredPermissions: FINANCE_ACCESS_PERMISSIONS, end: true },
];

const saxAdminSecurityNavItem: ShellNavItem = {
  path: '/admin/security/admins',
  label: 'Administradores SAX',
  icon: UserRoundCog,
  roles: ['admin'],
  requiredAdminCapabilities: ['sax_admins:manage'],
};

const adminNavItems: ShellNavItem[] = [
  { path: '/admin', label: 'Panel', icon: LayoutDashboard, roles: ['admin'], end: true },
  { path: '/admin/apis', label: 'Gateways SAX', icon: Server, roles: ['admin'] },
  { path: '/admin/business-agents', label: 'Agentes SAX', icon: Bot, roles: ['admin'] },
  { path: '/admin/clients', label: 'Clientes', icon: Building2, roles: ['admin'] },
  { path: '/admin/users', label: 'Usuarios', icon: Users, roles: ['admin'] },
  { path: '/admin/roles', label: 'Roles', icon: Shield, roles: ['admin'] },
  { path: '/admin/permissions', label: 'Permisos', icon: Key, roles: ['admin'] },
  { path: '/admin/audit', label: 'Auditoría', icon: FileClock, roles: ['admin'] },
  ...(SAX_ADMIN_SECURITY_ENABLED ? [saxAdminSecurityNavItem] : []),
];

const clientNavItems: ShellNavItem[] = [
  hubHomeNavItem,
  ...productionNavItems,
  ...businessAgentsNavItems,
  ...financeNavItems,
];

const clientFooterNavItems: ShellNavItem[] = [
  ...productionFooterNavItems,
];

const adminShellNavItems: ShellNavItem[] = [
  hubHomeNavItem,
  ...adminNavItems,
];

function ModuleLoadingFallback() {
  return (
    <div className="flex min-h-full items-center justify-center bg-[var(--bg)] px-6 py-10">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--primary)]"
        role="status"
        aria-label="Cargando"
      />
    </div>
  );
}

function LazyOutlet() {
  return (
    <Suspense fallback={<ModuleLoadingFallback />}>
      <Outlet />
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <SettingsProvider>
        <AuthProvider>
          <SessionTimeoutProvider>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/mfa" element={<AdminMfaPrompt fallbackPath="/" />} />

                <Route element={<AuthenticatedRoute><Outlet /></AuthenticatedRoute>}>
                  <Route path="/" element={<HubHomePage />} />
                </Route>

                <Route element={<ClientRoute><AppShell variant="client" navItems={clientNavItems} footerNavItems={clientFooterNavItems}><LazyOutlet /></AppShell></ClientRoute>}>
                  <Route element={<ModuleRoute moduleId={PRODUCTION_MODULE_ID} requiredPermissions={PRODUCTION_ACCESS_PERMISSIONS}><ProductionModuleLayout /></ModuleRoute>}>
                    <Route path="/dashboard" element={<ProductionDashboardPage />} />
                    <Route path="/kanban" element={<ProductionKanbanPage />} />
                    <Route path="/list" element={<ProductionListPage />} />
                    <Route path="/stations" element={<ProductionStationsPage />} />
                    <Route path="/reports" element={<ProductionReportsPage />} />
                    <Route path="/settings" element={<ProductionSettingsPage />} />
                  </Route>

                  <Route element={<ModuleRoute moduleId={BUSINESS_AGENTS_MODULE_ID} requiredPermissions={BUSINESS_AGENTS_ACCESS_PERMISSIONS}><BusinessAgentsModuleLayout /></ModuleRoute>}>
                    <Route path="/agentes" element={<BusinessAgentsHomePage />} />
                  </Route>

                  <Route path="/finanzas" element={<ModuleRoute moduleId={FINANCE_MODULE_ID} requiredPermissions={FINANCE_ACCESS_PERMISSIONS}><FinanceCashflowRoute /></ModuleRoute>} />
                  <Route path="/finanzas/:sheetId" element={<ModuleRoute moduleId={FINANCE_MODULE_ID} requiredPermissions={FINANCE_ACCESS_PERMISSIONS}><FinanceCashflowRoute /></ModuleRoute>} />
                </Route>

                <Route element={<AdminRoute><AppShell variant="admin" navItems={adminShellNavItems}><LazyOutlet /></AppShell></AdminRoute>}>
                  <Route element={<AdminModuleLayout />}>
                    <Route path="/admin" element={<AdminDashboardPage />} />
                    <Route path="/admin/apis" element={<AdminInstalledApisPage />} />
                    <Route path="/admin/business-agents" element={<AdminBusinessAgentsPage />} />
                    <Route path="/admin/clients" element={<AdminClientListPage />} />
                    <Route path="/admin/clients/:id" element={<AdminClientDetailPage />} />
                    <Route path="/admin/users" element={<AdminUserListPage />} />
                    <Route path="/admin/roles" element={<AdminRoleListPage />} />
                    <Route path="/admin/permissions" element={<AdminPermissionListPage />} />
                    <Route path="/admin/audit" element={<AdminAuditLogPage />} />
                    {SAX_ADMIN_SECURITY_ENABLED ? (
                      <Route path="/admin/security/admins" element={<SecurityOwnerRoute><SaxAdminSecurityPage /></SecurityOwnerRoute>} />
                    ) : null}
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
          </SessionTimeoutProvider>
        </AuthProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}
